import { useEffect, useMemo } from 'react';

import { useAuth } from './auth/AuthProvider.jsx';
import { hasPermission } from './auth/roles.js';
import { CLIENT_MODE, ENABLED_TABS } from './constants/env.js';
import { APP_TABS } from './constants/tabs.js';
import AuditLog from './pages/AuditLog';
import Dashboard from './pages/Dashboard';
import Extraction from './pages/Extraction';
import Records from './pages/Records';
import RecordDetail from './pages/RecordDetail';
import Rules from './pages/Rules';
import { useUrlState } from './url';
import './App.css';

function defaultTabFor(visibleTabs) {
  if (visibleTabs.length === 0) return null;
  if (!CLIENT_MODE) {
    const dash = visibleTabs.find((t) => t.id === 'dashboard');
    if (dash) return 'dashboard';
  }
  return visibleTabs[0].id;
}

export default function App() {
  const { role } = useAuth();
  const { segments, navigate } = useUrlState();

  // Compute the tabs this user can see. Build-time ENABLED_TABS still acts
  // as a feature flag (e.g., audit was hidden on Free tier); on top of it,
  // each tab's requiredPermission must be satisfied by the user's role.
  const visibleTabs = useMemo(
    () => APP_TABS.filter(
      (t) => ENABLED_TABS.has(t.id) && hasPermission(role, t.requiredPermission),
    ),
    [role],
  );
  const visibleTabIds = useMemo(
    () => new Set(visibleTabs.map((t) => t.id)),
    [visibleTabs],
  );

  // The first path segment IS the tab. Anything unrecognised (bare `/`, a
  // stale bookmark, a tab this role can't see) resolves to null and the
  // effect below rewrites the URL to the default tab.
  const requestedTab = segments[0] || null;
  const tab = requestedTab && visibleTabIds.has(requestedTab) ? requestedTab : null;

  // A billing record on its own page.
  //   /records/k/<token>  the SHAREABLE form — a key that survives the nightly
  //                       reprocess, which rebuilds billing.records and throws
  //                       every id away. This is what "Copy link" hands out.
  //   /records/<id>       still works for in-session navigation, and the page
  //                       rewrites it to the /k/ form on arrival so anything
  //                       copied from the address bar is already durable.
  // The two can never collide: 'k' is not a run of digits.
  const isRecords = tab === 'records';
  const recordKey = isRecords && segments[1] === 'k' && /^[A-Za-z0-9_-]+$/.test(segments[2] || '')
    ? segments[2] : null;
  const recordId = isRecords && /^\d+$/.test(segments[1] || '') ? segments[1] : null;

  // Land the user somewhere real: no tab in the URL, an unknown page, or a
  // tab their role can't see (role changed mid-session via PIM activation, or
  // the link came from someone with more access). Replace rather than push —
  // a redirect isn't a place the Back button should return to.
  useEffect(() => {
    if (tab) return;
    const fallback = defaultTabFor(visibleTabs);
    if (fallback) navigate([fallback], {}, { replace: true });
  }, [tab, visibleTabs, navigate]);

  return (
    <div className="app">
      <header className="header">
        <img
          src="/PSC Group Logo Horizontal Color.png"
          alt="PSC Group"
          className="header-logo"
        />
        <div style={{ flex: 1 }} />
        <div className="header-text">
          <h1>Billing Integration</h1>
          <span className="subtitle">DAYFORCE &rarr; NETSUITE</span>
        </div>
      </header>

      <nav className="nav">
        {visibleTabs.map((t) => (
          <a
            key={t.id}
            href={`/${t.id}`}
            className={`nav-btn ${tab === t.id ? 'active' : ''}`}
            // Real hrefs so every tab can be middle-clicked into a new tab or
            // copied from the context menu; the click handler keeps the
            // in-app navigation instant (no full reload).
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate([t.id]);
            }}
          >
            {t.label}
          </a>
        ))}
      </nav>

      <main className="main">
        {tab === 'dashboard'  && <Dashboard />}
        {tab === 'extraction' && <Extraction />}
        {tab === 'records' && (
          (recordId || recordKey)
            ? <RecordDetail recordId={recordId} recordKey={recordKey} />
            : <Records />
        )}
        {tab === 'rules' && <Rules />}
        {tab === 'audit' && <AuditLog />}
      </main>
    </div>
  );
}
